import React, { useState, useEffect } from 'react';
import { authAPI } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Profile = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: ''
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        fetchUserProfile();
    }, []);

    const fetchUserProfile = async () => {
        setLoading(true);
        try {
            const response = await authAPI.me();
            if (response.data.success) {
                const userData = response.data.data;
                setUser(userData);
                setFormData({
                    name: userData.name || '',
                    email: userData.email || '',
                    phone: userData.phone || '',
                    address: userData.address || ''
                });
            } else {
                toast.error(response.data.message || 'Failed to load profile');
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
            if (error.response?.status === 401) {
                toast.error('Session expired. Please login again.');
                setTimeout(() => {
                    window.location.href = '/login';
                }, 2000);
            } else {
                toast.error(error.response?.data?.message || 'Failed to load profile');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData({ ...formData, [id]: value });
        if (errors[id]) {
            setErrors({ ...errors, [id]: '' });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUpdating(true);
        setErrors({});

        try {
            const response = await authAPI.updateProfile(formData);
            console.log('Update response:', response.data);

            if (response.data.success) {
                toast.success('Profile updated successfully!');

                const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                const updatedUser = { ...storedUser, ...formData };
                localStorage.setItem('user', JSON.stringify(updatedUser));

                await fetchUserProfile();
            } else {
                toast.error(response.data.message || 'Failed to update profile');
            }
        } catch (error) {
            console.error('Update error:', error);
            if (error.response?.data?.errors) {
                setErrors(error.response.data.errors);
                toast.error('Please fix the validation errors');
            } else if (error.response?.status === 401) {
                toast.error('Session expired. Please login again.');
                setTimeout(() => {
                    window.location.href = '/login';
                }, 2000);
            } else {
                toast.error(error.response?.data?.message || 'Failed to update profile');
            }
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return (
            <div className="main-content">
                <ToastContainer />
                <div className="container py-4">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading profile...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main-content" style={{ background: '#f8f9fa', minHeight: '100vh' }}>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
            />

            <div className="container-fluid px-4 py-4">
                {/* Page Header */}
                <div className="mb-4">
                    <h1 className="page-title">
                        <i className="fas fa-key me-2"></i> My Profile
                    </h1>
                    <p className="text-muted">View and update your profile information</p>
                </div>

                <div className="row g-4">
                    {/* Profile Sidebar */}
                    <div className="col-lg-4">
                        <div className="card shadow-sm border-0 rounded-3" style={{ overflow: 'hidden' }}>
                            <div className="card-body p-0">
                                {/* Cover Image */}
                                <div style={{
                                    height: '100px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                }}></div>

                                {/* Avatar Section */}
                                <div className="text-center" style={{ marginTop: '-50px', marginBottom: '20px' }}>
                                    <div className="position-relative d-inline-block">
                                        <div
                                            className="rounded-circle bg-white shadow-sm d-flex align-items-center justify-content-center mx-auto"
                                            style={{
                                                width: '100px',
                                                height: '100px',
                                                border: '4px solid white'
                                            }}
                                        >
                                            <div
                                                className="rounded-circle d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: '92px',
                                                    height: '92px',
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                                }}
                                            >
                                                <i className="fas fa-user fa-3x text-white"></i>
                                            </div>
                                        </div>
                                        <button
                                            className="btn btn-sm btn-light rounded-circle position-absolute bottom-0 end-0 shadow-sm"
                                            style={{ transform: 'translate(0, 0)' }}
                                            onClick={() => document.getElementById('avatarUpload')?.click()}
                                        >
                                            <i className="fas fa-camera fa-xs"></i>
                                        </button>
                                        <input type="file" id="avatarUpload" style={{ display: 'none' }} accept="image/*" />
                                    </div>
                                </div>

                                {/* User Info */}
                                <div className="text-center px-4 pb-3">
                                    <h3 className="fw-bold mb-1">{user?.name}</h3>
                                    <span className="badge bg-primary bg-gradient px-3 py-2 mb-3">
                                        {user?.role_display || user?.role}
                                    </span>
                                    <p className="text-muted small mb-2">
                                        <i className="fas fa-id-card me-1"></i> Staff ID: {user?.staff_id}
                                    </p>
                                </div>

                                <hr className="my-2" />

                                {/* Contact Info */}
                                <div className="px-4 py-3">
                                    <h6 className="fw-bold mb-3 text-uppercase small text-muted">
                                        <i className="fas fa-address-card me-2"></i> Contact Information
                                    </h6>

                                    <div className="mb-3">
                                        <div className="d-flex align-items-center mb-2">
                                            <div className="rounded-circle p-2 me-3" style={{ background: '#e8f4f8', width: '35px', height: '35px', textAlign: 'center' }}>
                                                <i className="fas fa-envelope text-primary"></i>
                                            </div>
                                            <div>
                                                <label className="text-muted small mb-0">Email Address</label>
                                                <p className="mb-0 fw-semibold">{user?.email}</p>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center mb-2">
                                            <div className="rounded-circle p-2 me-3" style={{ background: '#e8f4f8', width: '35px', height: '35px', textAlign: 'center' }}>
                                                <i className="fas fa-phone text-success"></i>
                                            </div>
                                            <div>
                                                <label className="text-muted small mb-0">Phone Number</label>
                                                <p className="mb-0 fw-semibold">{user?.phone || 'Not provided'}</p>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center mb-2">
                                            <div className="rounded-circle p-2 me-3" style={{ background: '#e8f4f8', width: '35px', height: '35px', textAlign: 'center' }}>
                                                <i className="fas fa-briefcase text-warning"></i>
                                            </div>
                                            <div>
                                                <label className="text-muted small mb-0">Designation</label>
                                                <p className="mb-0 fw-semibold">{user?.designation || 'Not specified'}</p>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center">
                                            <div className="rounded-circle p-2 me-3" style={{ background: '#e8f4f8', width: '35px', height: '35px', textAlign: 'center' }}>
                                                <i className="fas fa-building text-info"></i>
                                            </div>
                                            <div>
                                                <label className="text-muted small mb-0">Department</label>
                                                <p className="mb-0 fw-semibold">{user?.department || 'Not specified'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Edit Profile Form */}
                    <div className="col-lg-8">
                        <div className="card shadow-sm border-0 rounded-3">
                            <div className="card-header bg-white border-0 pt-4 pb-0 px-4">
                                <h5 className="fw-bold mb-0">
                                    <i className="fas fa-edit me-2 text-primary"></i>
                                    Edit Profile Information
                                </h5>
                                <p className="text-muted small mt-2 mb-0">Update your personal information</p>
                            </div>
                            <div className="card-body p-4">
                                <form onSubmit={handleSubmit}>
                                    <div className="row g-4">
                                        {/* Full Name */}
                                        <div className="col-12">
                                            <label className="form-label fw-semibold required-field">
                                                <i className="fas fa-user me-1 text-muted"></i> Full Name
                                            </label>
                                            <input
                                                type="text"
                                                id="name"
                                                className={`form-control form-control-lg ${errors.name ? 'is-invalid' : ''}`}
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                placeholder="Enter your full name"
                                                style={{ borderRadius: '10px' }}
                                            />
                                            {errors.name && <div className="invalid-feedback">{errors.name[0]}</div>}
                                        </div>

                                        {/* Email (Disabled) */}
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">
                                                <i className="fas fa-envelope me-1 text-muted"></i> Email Address
                                            </label>
                                            <input
                                                type="email"
                                                id="email"
                                                className="form-control bg-light"
                                                value={formData.email}
                                                disabled
                                                style={{ borderRadius: '10px', background: '#f8f9fa' }}
                                            />
                                            <small className="text-muted">
                                                <i className="fas fa-info-circle me-1"></i> Email cannot be changed
                                            </small>
                                            {errors.email && <div className="invalid-feedback">{errors.email[0]}</div>}
                                        </div>

                                        {/* Phone Number */}
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">
                                                <i className="fas fa-phone me-1 text-muted"></i> Phone Number
                                            </label>
                                            <input
                                                type="tel"
                                                id="phone"
                                                className={`form-control form-control-lg ${errors.phone ? 'is-invalid' : ''}`}
                                                value={formData.phone}
                                                onChange={handleChange}
                                                placeholder="Enter your phone number"
                                                style={{ borderRadius: '10px' }}
                                            />
                                            {errors.phone && <div className="invalid-feedback">{errors.phone[0]}</div>}
                                        </div>

                                        {/* Address */}
                                        <div className="col-12">
                                            <label className="form-label fw-semibold">
                                                <i className="fas fa-map-marker-alt me-1 text-muted"></i> Address
                                            </label>
                                            <textarea
                                                id="address"
                                                className={`form-control ${errors.address ? 'is-invalid' : ''}`}
                                                rows="4"
                                                value={formData.address}
                                                onChange={handleChange}
                                                placeholder="Enter your complete address"
                                                style={{ borderRadius: '10px' }}
                                            ></textarea>
                                            {errors.address && <div className="invalid-feedback">{errors.address[0]}</div>}
                                        </div>



                                        {/* Action Buttons */}
                                        <div className="col-12">
                                            <hr className="my-2" />
                                            <div className="d-flex justify-content-end gap-3 mt-3">
                                                <button
                                                    type="button"
                                                    className="btn btn-light px-4 py-2"
                                                    onClick={() => {
                                                        setFormData({
                                                            name: user?.name || '',
                                                            email: user?.email || '',
                                                            phone: user?.phone || '',
                                                            address: user?.address || ''
                                                        });
                                                        setErrors({});
                                                    }}
                                                    style={{ borderRadius: '8px' }}
                                                >
                                                    <i className="fas fa-undo-alt me-2"></i>
                                                    Reset
                                                </button>
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary px-4 py-2"
                                                    disabled={updating}
                                                    style={{ borderRadius: '8px' }}
                                                >
                                                    <i className={`fas ${updating ? 'fa-spinner fa-spin' : 'fa-save'} me-2`}></i>
                                                    {updating ? 'Saving...' : 'Update Profile'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>


                    </div>
                </div>
            </div>

            <style jsx>{`
                .required-field::after {
                    content: ' *';
                    color: red;
                }
                
                .form-control:focus {
                    border-color: #667eea;
                    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
                }
                
                .btn-primary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    border: none;
                }
                
                .btn-primary:hover {
                    background: linear-gradient(135deg, #5a67d8 0%, #6b46a0 100%);
                    transform: translateY(-1px);
                    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
                }
                
                .btn-primary:active {
                    transform: translateY(0);
                }
                
                .card {
                    transition: transform 0.2s, box-shadow 0.2s;
                }
                
                .card:hover {
                    box-shadow: 0 8px 20px rgba(0,0,0,0.1) !important;
                }
                
                .badge {
                    font-weight: 500;
                    letter-spacing: 0.5px;
                }
            `}</style>
        </div>
    );
};

export default Profile;